import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutGrid, Table as TableIcon, Plus, Download, Filter, X, Bookmark, Brain, ScanSearch, ClipboardCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useReference } from "@/context/ReferenceContext";
import { useUniversalIntake } from "@/context/UniversalIntakeContext";
import { fmtCurrency, fmtDate, exportToCsv } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { RecordDialog } from "@/components/RecordDialog";
import { AddLeadDialog } from "@/components/AddLeadDialog";
import { InstaBrainPanel } from "@/components/InstaBrainPanel";
import { PageHeader } from "@/components/CrudModule";
import { STAGES, LEAD_FIELDS } from "@/config/modules";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const TABLE_COLS = [
  { key: "display_id", label: "Lead ID", type: "mono" },
  { key: "client_name", label: "Client" },
  { key: "state", label: "State" },
  { key: "lead_source", label: "Source" },
  { key: "assigned_agent", label: "Owner" },
  { key: "stage", label: "Stage", type: "badge" },
  { key: "next_action", label: "Next Action" },
  { key: "next_action_date", label: "Next Date", type: "date" },
  { key: "last_activity", label: "Last Activity", type: "date" },
  { key: "notes", label: "Notes" },
];

const ALL = "__all__";

const STAGE_ACCENT = {
  "New Lead": "border-t-[#315A7D]", "Contacted": "border-t-[#5B8AB0]",
  "Pending Underwriting": "border-t-amber-500", "Declined - Requote Needed": "border-t-red-500",
  "Issued / Active": "border-t-emerald-500", "Closed Lost": "border-t-slate-400",
};

const QUAL_STAGES = ["New", "Attempting Contact", "Contacted", "Qualified", "Unqualified",
  "Appointment Scheduled", "No Show", "Application Started", "Converted", "Closed"];

function QualifyDialog({ lead, onClose, onSaved }) {
  const [f, setF] = useState({
    stage: lead.stage || "New", qualification_outcome: lead.qualification_outcome || "",
    qualification_reason: lead.qualification_reason || "", disqualification_reason: lead.disqualification_reason || "",
    lead_priority: lead.lead_priority || "", next_action_date: lead.next_action_date || "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      await api.post(`/leads/${lead.id}/qualify`, payload);
      toast.success("Qualification saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4" data-testid="qualify-dialog">
      <div className="w-full max-w-md space-y-3 rounded-xl border bg-card p-5 text-sm">
        <div className="flex items-center justify-between font-semibold"><span>Qualify · {lead.client_name || lead.display_id}</span>
          <button onClick={onClose} data-testid="qualify-close"><X className="h-4 w-4" /></button></div>
        <div><label className="text-xs text-muted-foreground">Stage</label>
          <Select value={f.stage} onValueChange={(v) => set("stage", v)}>
            <SelectTrigger data-testid="qualify-stage"><SelectValue /></SelectTrigger>
            <SelectContent>{QUAL_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs text-muted-foreground">Outcome</label>
            <Select value={f.qualification_outcome || "none"} onValueChange={(v) => set("qualification_outcome", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="qualify-outcome"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="qualified">Qualified</SelectItem><SelectItem value="unqualified">Unqualified</SelectItem></SelectContent>
            </Select></div>
          <div><label className="text-xs text-muted-foreground">Priority</label>
            <Select value={f.lead_priority || "none"} onValueChange={(v) => set("lead_priority", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="qualify-priority"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
            </Select></div>
        </div>
        <div><label className="text-xs text-muted-foreground">Qualification reason</label>
          <Input value={f.qualification_reason} onChange={(e) => set("qualification_reason", e.target.value)} data-testid="qualify-reason" /></div>
        {f.qualification_outcome === "unqualified" && (
          <div><label className="text-xs text-muted-foreground">Disqualification reason</label>
            <Input value={f.disqualification_reason} onChange={(e) => set("disqualification_reason", e.target.value)} data-testid="qualify-disqual" /></div>
        )}
        <div><label className="text-xs text-muted-foreground">Next action date</label>
          <Input type="date" value={(f.next_action_date || "").slice(0, 10)} onChange={(e) => set("next_action_date", e.target.value)} data-testid="qualify-next-date" /></div>
        <p className="text-xs text-muted-foreground">Advisory only — not an eligibility or coverage guarantee.</p>
        <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="qualify-save">{busy ? "Saving…" : "Save qualification"}</Button></div>
      </div>
    </div>
  );
}

export default function LeadPipeline() {
  const { user } = useAuth();
  const { opts } = useReference();
  const { openIntake } = useUniversalIntake();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState("table");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [instaFor, setInstaFor] = useState(null);
  const [qualifyFor, setQualifyFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [viewName, setViewName] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [filters, setFilters] = useState({
    owner: ALL, stage: ALL, lead_source: ALL, state: ALL, carrier: ALL, due: "",
  });

  useEffect(() => {
    const next = {};
    ["owner", "stage", "lead_source", "state", "carrier"].forEach((k) => {
      const v = searchParams.get(k);
      if (v) next[k] = v;
    });
    const due = searchParams.get("due");
    if (due) next.due = due;
    if (Object.keys(next).length) {
      setFilters((f) => ({ ...f, ...next }));
      setShowFilters(true);
    }
    if (searchParams.get("view") === "kanban") setView("kanban");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canWrite = ["admin", "ops_manager", "agent", "bpo"].includes(user.role);
  const canDelete = ["admin", "ops_manager"].includes(user.role);

  const load = () => {
    setLoading(true);
    setSelectedIds(new Set());
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== ALL) params[k] = v; });
    api.get("/leads", { params })
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filters]);
  useEffect(() => { if (view !== "table") setSelectedIds(new Set()); }, [view]);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (row) => { setEditing(row); setDialogOpen(true); };

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing) await api.put(`/leads/${editing.id}`, form);
      else await api.post("/leads", form);
      toast.success(editing ? "Lead updated" : "Lead created");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const moveStage = async (leadId, newStage) => {
    const lead = rows.find((r) => r.id === leadId);
    if (!lead || lead.stage === newStage) return;
    setRows((rs) => rs.map((r) => (r.id === leadId ? { ...r, stage: newStage } : r)));
    try {
      await api.put(`/leads/${leadId}`, { stage: newStage });
      toast.success(`Moved to “${newStage}”`);
    } catch (e) {
      toast.error("Move failed");
      load();
    }
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({ owner: ALL, stage: ALL, lead_source: ALL, state: ALL, carrier: ALL, due: "" });
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => v && v !== ALL).length;

  const saveView = async () => {
    const f = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== ALL) f[k] = v; });
    try {
      await api.post("/views", { name: viewName || "My View", route: "/pipeline", filters: f });
      toast.success("View saved — pinned to your dashboard");
      setSaveViewOpen(false);
      setViewName("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    STAGES.forEach((s) => (g[s] = []));
    rows.forEach((r) => { (g[r.stage] || (g[r.stage] = [])).push(r); });
    return g;
  }, [rows]);

  const allSelected = canDelete && rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someSelected = canDelete && !allSelected && rows.some((r) => selectedIds.has(r.id));

  const toggleRow = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    setSelectedIds(checked ? new Set(rows.map((r) => r.id)) : new Set());
  };

  const deleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected lead(s)? They can be restored from the Deleted view in operational modules.`)) return;
    setBulkBusy(true);
    let succeeded = 0;
    let failed = 0;
    try {
      for (let i = 0; i < ids.length; i += 20) {
        const results = await Promise.allSettled(ids.slice(i, i + 20).map((id) => api.delete(`/leads/${id}`)));
        results.forEach((r) => {
          if (r.status === "fulfilled") succeeded += 1;
          else failed += 1;
        });
      }
      if (succeeded) toast.success(`Deleted ${succeeded} lead(s).`);
      if (failed) toast.error(`${failed} lead(s) could not be deleted.`);
      load();
    } finally {
      setBulkBusy(false);
    }
  };

  const FilterSelect = ({ k, label, options }) => (
    <Select value={filters[k]} onValueChange={(v) => setF(k, v)}>
      <SelectTrigger className="w-full sm:w-40" data-testid={`filter-${k}`}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All {label}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div data-testid="lead-pipeline-page">
      <PageHeader title="Lead Pipeline" subtitle="Track every lead from intake to issued policy.">
        <div className="flex rounded-md border border-border bg-card p-0.5">
          <button
            onClick={() => setView("table")}
            data-testid="view-table-btn"
            className={`flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium transition-colors ${view === "table" ? "bg-primary text-white" : "text-muted-foreground"}`}
          >
            <TableIcon className="h-4 w-4" /> Table
          </button>
          <button
            onClick={() => setView("kanban")}
            data-testid="view-kanban-btn"
            className={`flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium transition-colors ${view === "kanban" ? "bg-primary text-white" : "text-muted-foreground"}`}
          >
            <LayoutGrid className="h-4 w-4" /> Kanban
          </button>
        </div>
        <Button variant="outline" onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters-btn">
          <Filter className="mr-1.5 h-4 w-4" /> Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
        </Button>
        <Button variant="outline" onClick={() => exportToCsv("leads.csv", rows, TABLE_COLS)} data-testid="export-btn">
          <Download className="mr-1.5 h-4 w-4" /> Export
        </Button>
        {canWrite && (
          <Button onClick={() => setAddLeadOpen(true)} data-testid="add-lead-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Add Lead
          </Button>
        )}
        {canWrite && (
          <Button className="bg-[#B28A3D] text-[#17324D] hover:bg-[#c99b45]" data-testid="pipeline-universal-intake" onClick={() => openIntake()}>
            <ScanSearch className="mr-1.5 h-4 w-4" /> Universal Intake
          </Button>
        )}
      </PageHeader>

      {showFilters && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
          <FilterSelect k="owner" label="Owners" options={opts.agents} />
          <FilterSelect k="stage" label="Stages" options={STAGES} />
          <FilterSelect k="lead_source" label="Sources" options={opts.sources} />
          <FilterSelect k="state" label="States" options={opts.states} />
          <FilterSelect k="carrier" label="Carriers" options={opts.carriers} />
          <input
            type="date"
            data-testid="filter-due"
            value={filters.due}
            onChange={(e) => setF("due", e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            title="Next action due on/before"
          />
          <Button variant="outline" size="sm" onClick={() => setSaveViewOpen(true)} data-testid="save-view-btn">
            <Bookmark className="mr-1 h-3.5 w-3.5" /> Save View
          </Button>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
              <X className="mr-1 h-3.5 w-3.5" /> Clear
            </Button>
          )}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{rows.length} lead(s)</span>
        {view === "table" && canDelete && selectedIds.size > 0 && (
          <div className="flex items-center gap-2" data-testid="lead-bulk-selection-bar">
            <span className="font-medium text-foreground">{selectedIds.size} selected</span>
            <Button size="sm" variant="destructive" onClick={deleteSelected} disabled={bulkBusy} data-testid="delete-selected-leads-btn">
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> {bulkBusy ? "Deleting…" : "Delete Selected"}
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="grid place-items-center h-48 text-muted-foreground">Loading…</div>
      ) : view === "table" ? (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                {canDelete && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={(v) => toggleAll(v === true)}
                      aria-label="Select all lead results"
                      data-testid="select-all-leads"
                    />
                  </TableHead>
                )}
                {TABLE_COLS.map((c) => <TableHead key={c.key} className="overline whitespace-nowrap">{c.label}</TableHead>)}
                <TableHead className="overline whitespace-nowrap">Placement</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer text-[13px]" onClick={() => openEdit(r)} data-testid={`lead-row-${r.id}`}>
                  {canDelete && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(r.id)}
                        onCheckedChange={(v) => toggleRow(r.id, v === true)}
                        aria-label={`Select ${r.display_id || r.id}`}
                        data-testid={`select-lead-${r.id}`}
                      />
                    </TableCell>
                  )}
                  {TABLE_COLS.map((c) => (
                    <TableCell key={c.key} className="whitespace-nowrap max-w-[220px] truncate">
                      {c.type === "badge" ? <StatusBadge value={r[c.key]} />
                        : c.type === "date" ? fmtDate(r[c.key])
                        : c.type === "mono" ? <span className="font-mono text-xs">{r[c.key]}</span>
                        : (r[c.key] || <span className="text-muted-foreground">—</span>)}
                    </TableCell>
                  ))}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                      onClick={() => setQualifyFor(r)} data-testid={`qualify-${r.id}`}>
                      <ClipboardCheck className="mr-1 h-3.5 w-3.5" /> Qualify
                    </Button>
                    <Button size="sm" variant={r.stage === "Ready to Quote" ? "default" : "ghost"}
                      className={`h-7 px-2 text-xs ${r.stage === "Ready to Quote" ? "bg-[#B28A3D] hover:bg-[#9c7833]" : ""}`}
                      onClick={() => setInstaFor(r)} data-testid={`instabrain-${r.id}`}
                      title={r.instabrain_review?.reviewed ? "InstaBrain review on file" : "Open InstaBrain for Quote / Placement Review"}>
                      <Brain className="mr-1 h-3.5 w-3.5" /> {r.instabrain_review?.reviewed ? "Reviewed" : "InstaBrain"}
                    </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4 kanban-scroll">
          {STAGES.map((stage) => (
            <div
              key={stage}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragId) moveStage(dragId, stage); setDragId(null); }}
              className="w-[280px] shrink-0 rounded-lg bg-secondary/50"
              data-testid={`kanban-col-${stage}`}
            >
              <div className={`flex items-center justify-between rounded-t-lg border-t-2 bg-card px-3 py-2 ${STAGE_ACCENT[stage] || "border-t-slate-300"}`}>
                <span className="text-xs font-semibold truncate">{stage}</span>
                <span className="ml-2 rounded-full bg-secondary px-1.5 text-[11px] text-muted-foreground">{grouped[stage]?.length || 0}</span>
              </div>
              <div className="space-y-2 p-2 min-h-[60px]">
                {grouped[stage]?.map((lead) => (
                  <div
                    key={lead.id}
                    draggable={canWrite}
                    onDragStart={() => setDragId(lead.id)}
                    onClick={() => openEdit(lead)}
                    data-testid={`kanban-card-${lead.id}`}
                    className="cursor-pointer rounded-md border border-border bg-card p-2.5 text-sm shadow-sm transition-shadow hover:shadow-md"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate">{lead.client_name}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">{lead.display_id}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <span>{lead.state}</span><span>·</span><span className="truncate">{lead.lead_source}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">{lead.assigned_agent}</span>
                      {lead.quoted_premium ? <span className="font-medium text-foreground">{fmtCurrency(lead.quoted_premium)}/mo</span> : null}
                    </div>
                    {lead.next_action && (
                      <div className="mt-2 rounded bg-secondary px-1.5 py-1 text-[11px] text-muted-foreground truncate">
                        ▸ {lead.next_action} · {fmtDate(lead.next_action_date)}
                      </div>
                    )}
                    {canWrite && (
                      <label className="mt-3 block border-t pt-2 text-[11px] text-muted-foreground"
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                        onTouchStart={(event) => event.stopPropagation()}>
                        Move to stage
                        <select
                          aria-label={`Move ${lead.client_name || lead.display_id} to stage`}
                          data-testid={`kanban-stage-${lead.id}`}
                          className="mt-1 block min-h-10 w-full max-w-full rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground"
                          value={lead.stage || "New Lead"}
                          onChange={(event) => { event.stopPropagation(); moveStage(lead.id, event.target.value); }}
                        >
                          {STAGES.map((stageName) => (
                            <option value={stageName} key={stageName}>{stageName}</option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <RecordDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={editing ? `Edit Lead · ${editing.display_id || ""}` : "New Lead"}
        fields={LEAD_FIELDS}
        initial={editing}
        onSave={save}
        saving={saving}
        calc
        entityType="leads"
      />

      <Dialog open={saveViewOpen} onOpenChange={setSaveViewOpen}>
        <DialogContent className="max-w-md" data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Save Current View</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Saves the current filters as a personal worklist pinned to your dashboard.
            </p>
            <Input
              data-testid="save-view-name"
              placeholder="View name (e.g. My TX Final Expense)"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveViewOpen(false)}>Cancel</Button>
            <Button onClick={saveView} data-testid="save-view-confirm">Save View</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {addLeadOpen && <AddLeadDialog onClose={() => setAddLeadOpen(false)} onSaved={load} />}
      {instaFor && <InstaBrainPanel lead={instaFor} onClose={() => setInstaFor(null)} onSaved={load} />}
      {qualifyFor && <QualifyDialog lead={qualifyFor} onClose={() => setQualifyFor(null)} onSaved={load} />}
    </div>
  );
}
